import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { ArrowLeft, CheckCircle, XCircle, Play, Tag01, Save01, Download01, AlertCircle, Edit05 } from '@untitledui/icons';
import { BackLink } from '@/components/base/back-link/back-link';
import { useBackLink } from '@/hooks/use-back-link';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { Select } from '@/components/base/select/select';
import { Input } from '@/components/base/input/input';
import { PdfViewer } from '@/components/application/pdf-viewer/pdf-viewer';
import { useTranslation } from 'react-i18next';
import {
    useTrainingDocument,
    useTrainingDocumentPreviewUrl,
    useExtractionStrategies,
    useRunExtraction,
    useClassifyTrainingDocument,
    useTrainingDocumentJobs,
    useUpdateTrainingDocument,
    useSplitTrainingDocument,
    useGroundTruth,
    useSaveGroundTruth,
    useSaveGroundTruthDraft,
} from '@/hooks/api/use-document-training';
import { useDocumentSchemas } from '@/hooks/api/use-document-schemas';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { useSchemaAttributes } from '@/hooks/api/use-schema-attributes';
import { TrainingStatus as TrainingStatusEnum, type DetectedDocumentType } from '@/types/api/document-training';
import type { SchemaAttributeResponse } from '@/types/api/document-schemas';
import { useToast } from '@/hooks/use-toast';
import { JobHistorySection } from './components/job-history-section';
import { SplitDocumentModal } from './components/split-document-modal';

// ─── Importance helpers ────────────────────────────────────────────────
const IMPORTANCE_CONFIG = {
    1: { label: 'Critical', dotColor: 'bg-error-500', ringColor: 'ring-error-200', textColor: 'text-error-700' },
    2: { label: 'Important', dotColor: 'bg-warning-500', ringColor: 'ring-warning-200', textColor: 'text-warning-700' },
    3: { label: 'Optional', dotColor: 'bg-gray-400', ringColor: 'ring-gray-200', textColor: 'text-tertiary' },
} as const;

function ImportanceDot({ level }: { level: number }) {
    const cfg = IMPORTANCE_CONFIG[level as keyof typeof IMPORTANCE_CONFIG] ?? IMPORTANCE_CONFIG[3];
    return (
        <span
            className={`inline-block size-2.5 rounded-full ${cfg.dotColor} ring-2 ${cfg.ringColor}`}
            title={cfg.label}
        />
    );
}

// ─── Ground Truth Field Row ────────────────────────────────────────────
interface FieldRowProps {
    attr: SchemaAttributeResponse;
    value: string;
    onChange: (key: string, value: string) => void;
}

function GroundTruthFieldRow({ attr, value, onChange }: FieldRowProps) {
    const key = attr.attribute_key ?? attr.document_attribute_id;
    const label = attr.custom_label || attr.attribute_label || key;

    return (
        <div className="flex items-start gap-3 py-2.5 px-3 rounded-lg hover:bg-secondary/40 transition-colors group">
            <div className="pt-2.5">
                <ImportanceDot level={attr.importance_level} />
            </div>
            <div className="flex-1 min-w-0">
                <label htmlFor={`gt-${key}`} className="block text-sm font-medium text-primary mb-1 truncate">
                    {label}
                </label>
                <input
                    id={`gt-${key}`}
                    type="text"
                    value={value}
                    onChange={(e) => onChange(key, e.target.value)}
                    placeholder="—"
                    className="w-full px-3 py-1.5 text-sm text-primary bg-primary border border-secondary rounded-md
                               focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand
                               placeholder:text-quaternary transition-all"
                />
            </div>
        </div>
    );
}

// ─── Main Page ─────────────────────────────────────────────────────────
export const TrainingDocumentDetailPage = () => {
    const { trainingDocId } = useParams<{ trainingDocId: string }>();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const { goBack } = useBackLink('/admin/documents/training');
    const toast = useToast();

    // UI state
    const [activeTab, setActiveTab] = useState<'ground-truth' | 'actions' | 'history'>('ground-truth');
    const [selectedStrategy, setSelectedStrategy] = useState<string>('vision');
    const [ocrOnly, setOcrOnly] = useState(false);
    const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
    const [detectedDocumentTypes, setDetectedDocumentTypes] = useState<DetectedDocumentType[]>([]);
    const [manualDocTypeId, setManualDocTypeId] = useState<string>('');
    const [editingDatasetName, setEditingDatasetName] = useState<string | null>(null);

    // Ground truth form state
    const [gtValues, setGtValues] = useState<Record<string, string>>({});
    const [gtNotes, setGtNotes] = useState('');
    const [gtOtherFields, setGtOtherFields] = useState<{ key: string; value: string }[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
    const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ─── Data fetching ────────────────────────────────────────────────
    const { data: document, isLoading, isError, refetch: refetchDocument } = useTrainingDocument(trainingDocId || '');
    const { data: previewUrl } = useTrainingDocumentPreviewUrl(trainingDocId);
    const { data: documentTypes = [] } = useDocumentTypes({ limit: 100 });
    const { data: extractionStrategies = [] } = useExtractionStrategies();
    const { data: jobs = [], isLoading: isLoadingJobs, isError: isJobsError, refetch: refetchJobs } = useTrainingDocumentJobs(trainingDocId);
    const { data: groundTruth, isLoading: isLoadingGT } = useGroundTruth(trainingDocId);

    // Mutations
    const runExtraction = useRunExtraction();
    const classifyDocument = useClassifyTrainingDocument();
    const updateDocument = useUpdateTrainingDocument();
    const splitDocument = useSplitTrainingDocument();
    const saveGroundTruth = useSaveGroundTruth();
    const saveDraft = useSaveGroundTruthDraft();

    // Schema + attributes
    const { data: schemas = [] } = useDocumentSchemas({
        document_type: document?.document_type || undefined,
        is_active: true,
        limit: 500,
    });

    const schemaId = document?.attribute_metadata?.document_schema_id as string | undefined
        ?? document?.document_schema_id ?? undefined;

    const schema = schemaId
        ? schemas.find(s => s.document_schema_id === schemaId)
        : schemas.find(s => s.is_default) ?? null;

    const { data: schemaAttributes = [] } = useSchemaAttributes(schemaId || '', {
        limit: 200,
    });

    const currentDocType = useMemo(
        () => documentTypes.find((dt) => dt.key === document?.document_type),
        [documentTypes, document?.document_type],
    );

    // ─── Group attributes by importance ───────────────────────────────
    const groupedAttributes = useMemo(() => {
        const groups: Record<number, SchemaAttributeResponse[]> = { 1: [], 2: [], 3: [] };
        for (const attr of schemaAttributes) {
            const level = attr.importance_level ?? 3;
            (groups[level] ??= []).push(attr);
        }
        // Sort within each group by display_order
        for (const arr of Object.values(groups)) {
            arr.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        }
        return groups;
    }, [schemaAttributes]);

    // ─── Pre-fill ground truth from existing data (prefer draft) ──────
    useEffect(() => {
        if (!isDirty && groundTruth) {
            // Prefer draft data over GCS data (draft = unsaved WIP)
            const source = groundTruth.draft?.structured_data
                ? groundTruth.draft as { structured_data: Record<string, unknown>; notes?: string; other_fields?: Record<string, unknown> }
                : groundTruth;

            const structuredData = (source.structured_data ?? {}) as Record<string, unknown>;
            const flat: Record<string, string> = {};
            const otherFields: { key: string; value: string }[] = [];
            const schemaKeys = new Set(schemaAttributes.map(a => a.attribute_key));

            for (const [k, v] of Object.entries(structuredData)) {
                const stringValue = typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '');
                if (schemaKeys.has(k)) {
                    flat[k] = stringValue;
                } else {
                    otherFields.push({ key: k, value: stringValue });
                }
            }

            // Restore "other_fields" from draft if present
            if (groundTruth.draft?.other_fields) {
                const draftOther = groundTruth.draft.other_fields as Record<string, unknown>;
                for (const [k, v] of Object.entries(draftOther)) {
                    if (!schemaKeys.has(k) && !otherFields.some(f => f.key === k)) {
                        otherFields.push({ key: k, value: String(v ?? '') });
                    }
                }
            }

            setGtValues(flat);
            setGtOtherFields(otherFields.length > 0 ? otherFields : []);
            setGtNotes(
                (groundTruth.draft ? (source as { notes?: string }).notes : groundTruth.notes) || ''
            );

            // Show existing draft timestamp
            if (groundTruth.draft_updated_at) {
                setDraftSavedAt(groundTruth.draft_updated_at);
            }
        }
    }, [groundTruth, schemaAttributes, isDirty]);

    // ─── Auto-save draft (debounced 3s) ────────────────────────────────
    useEffect(() => {
        if (!isDirty || !trainingDocId) return;

        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

        draftTimerRef.current = setTimeout(() => {
            const structured_data: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(gtValues)) {
                if (v.trim()) {
                    if (v.trim().startsWith('{') || v.trim().startsWith('[')) {
                        try { structured_data[k] = JSON.parse(v); continue; } catch { /* keep as string */ }
                    }
                    structured_data[k] = v;
                }
            }
            const other_fields: Record<string, unknown> = {};
            for (const f of gtOtherFields) {
                if (f.key.trim() && f.value.trim()) other_fields[f.key.trim()] = f.value;
            }

            saveDraft.mutate(
                { docId: trainingDocId, data: { structured_data, notes: gtNotes || undefined, other_fields } },
                { onSuccess: (res) => setDraftSavedAt(res.saved_at) }
            );
        }, 3000);

        return () => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        };
    }, [isDirty, gtValues, gtNotes, gtOtherFields, trainingDocId]);

    // Cleanup blob URL
    useEffect(() => {
        const currentUrl = previewUrl;
        return () => {
            if (currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
        };
    }, [previewUrl]);

    const isExcelFile = useMemo(() => {
        if (!document?.file_path) return false;
        const ext = document.file_path.split('.').pop()?.toLowerCase();
        return ext === 'xlsx' || ext === 'xls';
    }, [document?.file_path]);

    const isImageFile = useMemo(() => {
        if (!document?.file_path) return false;
        const ext = document.file_path.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif'].includes(ext || '');
    }, [document?.file_path]);

    // ─── Handlers ─────────────────────────────────────────────────────
    const handleFieldChange = useCallback((key: string, value: string) => {
        setGtValues(prev => ({ ...prev, [key]: value }));
        setIsDirty(true);
    }, []);

    const handleSaveGroundTruth = async () => {
        if (!trainingDocId) return;

        // Build structured_data from form values
        const structured_data: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(gtValues)) {
            if (v.trim()) {
                // Try to parse JSON objects/arrays
                if (v.trim().startsWith('{') || v.trim().startsWith('[')) {
                    try { structured_data[k] = JSON.parse(v); continue; } catch { /* keep as string */ }
                }
                structured_data[k] = v;
            }
        }
        // Add "other" fields
        for (const f of gtOtherFields) {
            if (f.key.trim() && f.value.trim()) {
                structured_data[f.key.trim()] = f.value;
            }
        }

        try {
            await saveGroundTruth.mutateAsync({
                docId: trainingDocId,
                data: { structured_data, notes: gtNotes || undefined },
            });
            setIsDirty(false);
            setDraftSavedAt(null);
            toast.success('Ground truth saved');
        } catch (error) {
            console.error('Error saving ground truth:', error);
            toast.error('Error saving ground truth');
        }
    };

    const handleExportJson = () => {
        const structured_data: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(gtValues)) {
            if (v.trim()) structured_data[k] = v;
        }
        for (const f of gtOtherFields) {
            if (f.key.trim() && f.value.trim()) structured_data[f.key.trim()] = f.value;
        }

        const json = JSON.stringify({
            document_id: trainingDocId,
            document_type: document?.document_type || 'unknown',
            schema_code: schema?.schema_code || 'default',
            source_file: document?.file_path?.split('/').pop() || '',
            notes: gtNotes,
            structured_data,
        }, null, 2);

        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = `${document?.file_path?.split('/').pop()?.replace(/\.[^.]+$/, '') || 'ground_truth'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleRunExtraction = async () => {
        if (!trainingDocId) return;
        try {
            await runExtraction.mutateAsync({ trainingDocId, strategy: selectedStrategy, schemaId: schema?.document_schema_id, ocrOnly });
            toast.success(t('training.detail.extraction.success', { defaultValue: 'Extraction completed successfully' }));
            refetchJobs();
        } catch (error) {
            console.error('Error running extraction:', error);
            toast.error(t('training.detail.extraction.error', { defaultValue: 'Error during extraction' }));
        }
    };

    const handleRunClassification = async () => {
        if (!trainingDocId) return;
        try {
            const result = await classifyDocument.mutateAsync(trainingDocId);
            if (result.is_multi_document && result.document_types_detected && result.document_types_detected.length > 1) {
                setDetectedDocumentTypes(result.document_types_detected);
                setIsSplitModalOpen(true);
                toast.success(t('training.detail.classification.multiDocumentDetected', {
                    defaultValue: 'Multi-document detected: {{count}} document types found.',
                    count: result.document_types_detected.length,
                }));
            } else {
                toast.success(t('training.detail.classification.success', {
                    defaultValue: 'Classification completed: {{type}} ({{confidence}}%)',
                    type: result.document_type || 'Unknown',
                    confidence: (result.confidence * 100).toFixed(1),
                }));
            }
            refetchDocument();
            refetchJobs();
        } catch (error) {
            console.error('Error running classification:', error);
            toast.error(t('training.detail.classification.error', { defaultValue: 'Error during classification' }));
        }
    };

    const handleSplitConfirm = async () => {
        if (!trainingDocId || detectedDocumentTypes.length === 0) return;
        try {
            const pageRanges = detectedDocumentTypes.map((docType) => ({
                start: docType.page_start ?? 1,
                end: docType.page_end ?? 1,
                document_type: docType.type,
                confidence: docType.confidence,
            }));
            const result = await splitDocument.mutateAsync({ docId: trainingDocId, pageRanges });
            toast.success(t('training.split.success', { defaultValue: 'Split into {{count}} documents', count: result.split_count }));
            setIsSplitModalOpen(false);
            setDetectedDocumentTypes([]);
            refetchDocument();
        } catch (error) {
            console.error('Error splitting document:', error);
            toast.error(t('training.split.error', { defaultValue: 'Error splitting document' }));
        }
    };

    const handleSetManualDocType = async () => {
        if (!trainingDocId || !manualDocTypeId) return;
        const selectedType = documentTypes.find(dt => dt.document_type_id === manualDocTypeId);
        if (!selectedType) return;
        try {
            await updateDocument.mutateAsync({
                docId: trainingDocId,
                data: { document_type: selectedType.key, document_type_id: manualDocTypeId },
            });
            toast.success(`Document type set to ${selectedType.name}`);
            setManualDocTypeId('');
            refetchDocument();
            refetchJobs();
        } catch (error) {
            console.error('Error setting document type:', error);
            toast.error('Error setting document type');
        }
    };

    const handleValidateDocument = async (status: TrainingStatusEnum) => {
        if (!trainingDocId) return;
        try {
            await updateDocument.mutateAsync({ docId: trainingDocId, data: { training_status: status } });
            toast.success(`Document status updated to ${status}`);
            refetchDocument();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Error updating document status');
        }
    };

    const getStatusBadge = (status: TrainingStatusEnum) => {
        const map: Record<string, { color: 'gray' | 'blue' | 'success' | 'error' | 'warning'; label: string }> = {
            pending: { color: 'gray', label: 'Pending' },
            in_progress: { color: 'warning', label: 'In Progress' },
            annotated: { color: 'blue', label: 'Annotated' },
            validated: { color: 'blue', label: 'Validated' },
            approved: { color: 'success', label: 'Approved' },
            rejected: { color: 'error', label: 'Rejected' },
        };
        const cfg = map[status] ?? map.pending;
        return <Badge color={cfg.color} size="sm">{cfg.label}</Badge>;
    };

    // ─── Loading / Error states ───────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
            </div>
        );
    }

    if (isError || !document) {
        return (
            <div className="text-center py-12 space-y-4">
                <h3 className="text-lg font-medium text-primary">Document not found</h3>
                <Button onClick={goBack} size="md" color="secondary">Back to List</Button>
            </div>
        );
    }

    const hasSchema = schemaAttributes.length > 0;

    // ─── Render ───────────────────────────────────────────────────────
    return (
        <div className="flex flex-col min-h-0" style={{ height: 'calc(100vh - 7rem)' }}>
            {/* Top bar — breadcrumb + actions */}
            <div className="flex-shrink-0 flex items-center justify-between pb-4 border-b border-secondary mb-4">
                <div className="flex items-center gap-2 min-w-0 text-sm">
                    <BackLink fallbackTo="/admin/documents/training" className="text-tertiary hover:text-primary">
                        Training Documents
                    </BackLink>
                    {currentDocType && (
                        <>
                            <span className="text-quaternary">/</span>
                            <span className="text-tertiary">{currentDocType.name}</span>
                        </>
                    )}
                    {schema && (
                        <>
                            <span className="text-quaternary">/</span>
                            <span className="text-tertiary">{schema.schema_name}</span>
                        </>
                    )}
                    <span className="text-quaternary">/</span>
                    <span className="text-primary font-medium truncate">{document.file_path.split('/').pop()}</span>
                    {getStatusBadge(document.training_status as TrainingStatusEnum)}
                    {document.ground_truth_path && (
                        <Badge color="success" size="sm">Ground Truth</Badge>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Button onClick={goBack} size="sm" color="secondary" iconLeading={ArrowLeft}>
                        Back
                    </Button>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
                {/* ─── LEFT: Document preview (fixed height, no scroll) ──── */}
                <div className="flex-1 min-w-0 flex flex-col rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden">
                    <div className="flex-1 min-h-0 p-4">
                        {previewUrl ? (
                            isExcelFile || isImageFile ? (
                                <div className="flex items-center justify-center h-full overflow-auto bg-secondary/20 rounded-lg">
                                    <img src={previewUrl} alt="Document preview" className="max-w-full max-h-full object-contain" />
                                </div>
                            ) : (
                                <PdfViewer key={previewUrl} fileUrl={previewUrl} polygons={[]} hideAddRectangle showZoomControls />
                            )
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── RIGHT: Tabbed panel (scrollable) ───────────────── */}
                <div className="w-[380px] flex-shrink-0 flex flex-col rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden">
                    {/* Tab bar */}
                    <div className="flex-shrink-0 flex border-b border-secondary">
                        {(['ground-truth', 'actions', 'history'] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                                    activeTab === tab
                                        ? 'text-brand border-b-2 border-brand'
                                        : 'text-tertiary hover:text-primary'
                                }`}
                            >
                                {tab === 'ground-truth' ? 'Ground Truth' : tab === 'actions' ? 'Actions' : 'History'}
                            </button>
                        ))}
                    </div>

                    {/* Tab content — scrollable */}
                    <div className="flex-1 overflow-y-auto">
                        {/* ═══ GROUND TRUTH TAB ═══ */}
                        {activeTab === 'ground-truth' && (
                            <div className="p-4 space-y-4">
                                {/* Draft indicator — top of tab */}
                                {(draftSavedAt || saveDraft.isPending) && (
                                    <div className="px-3 py-2 rounded-md bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800">
                                        <span className="text-xs font-medium text-brand-700 dark:text-brand-300">
                                            {saveDraft.isPending
                                                ? 'Saving draft...'
                                                : `Draft saved at ${new Date(draftSavedAt! + 'Z').toLocaleTimeString()}`
                                            }
                                        </span>
                                    </div>
                                )}

                                {/* Header info */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-tertiary">Type</span>
                                        <p className="font-medium text-primary">{currentDocType?.name || document.document_type || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-tertiary">Schema</span>
                                        <p className="font-medium text-primary">{schema?.schema_name || '—'}</p>
                                    </div>
                                </div>

                                {!hasSchema && (
                                    <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-50 dark:bg-warning-950/20 text-warning-700 dark:text-warning-400 text-sm">
                                        <AlertCircle className="size-4 mt-0.5 flex-shrink-0" />
                                        <span>No schema attributes found. Assign a document type and schema to see fields, or add free-form fields below.</span>
                                    </div>
                                )}

                                {isLoadingGT && (
                                    <div className="flex justify-center py-4">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand" />
                                    </div>
                                )}

                                {/* Schema-driven fields grouped by importance */}
                                {hasSchema && (
                                    <div className="space-y-4">
                                        {([1, 2, 3] as const).map((level) => {
                                            const attrs = groupedAttributes[level];
                                            if (!attrs || attrs.length === 0) return null;
                                            const cfg = IMPORTANCE_CONFIG[level];
                                            return (
                                                <div key={level}>
                                                    <div className="flex items-center gap-2 mb-1 px-3">
                                                        <ImportanceDot level={level} />
                                                        <span className={`text-xs font-semibold uppercase tracking-wider ${cfg.textColor}`}>
                                                            {cfg.label} ({attrs.length})
                                                        </span>
                                                    </div>
                                                    <div className="space-y-0.5">
                                                        {attrs.map((attr) => (
                                                            <GroundTruthFieldRow
                                                                key={attr.document_schema_attribute_id}
                                                                attr={attr}
                                                                value={gtValues[attr.attribute_key ?? attr.document_attribute_id] ?? ''}
                                                                onChange={handleFieldChange}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Other fields (free-form) */}
                                <div>
                                    <div className="flex items-center justify-between px-3 mb-2">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-tertiary">
                                            Other Fields
                                        </span>
                                        <button
                                            onClick={() => {
                                                setGtOtherFields(prev => [...prev, { key: '', value: '' }]);
                                                setIsDirty(true);
                                            }}
                                            className="text-xs text-brand hover:text-brand-600 font-medium"
                                        >
                                            + Add Field
                                        </button>
                                    </div>
                                    {gtOtherFields.map((f, i) => (
                                        <div key={i} className="flex items-center gap-2 py-1.5 px-3">
                                            <input
                                                type="text"
                                                value={f.key}
                                                onChange={(e) => {
                                                    setGtOtherFields(prev => prev.map((item, idx) => idx === i ? { ...item, key: e.target.value } : item));
                                                    setIsDirty(true);
                                                }}
                                                placeholder="field_key"
                                                className="w-1/3 px-2 py-1.5 text-sm text-primary bg-primary border border-secondary rounded-md font-mono
                                                           focus:outline-none focus:ring-2 focus:ring-brand/40 placeholder:text-quaternary"
                                            />
                                            <input
                                                type="text"
                                                value={f.value}
                                                onChange={(e) => {
                                                    setGtOtherFields(prev => prev.map((item, idx) => idx === i ? { ...item, value: e.target.value } : item));
                                                    setIsDirty(true);
                                                }}
                                                placeholder="value"
                                                className="flex-1 px-2 py-1.5 text-sm text-primary bg-primary border border-secondary rounded-md
                                                           focus:outline-none focus:ring-2 focus:ring-brand/40 placeholder:text-quaternary"
                                            />
                                            <button
                                                onClick={() => {
                                                    setGtOtherFields(prev => prev.filter((_, idx) => idx !== i));
                                                    setIsDirty(true);
                                                }}
                                                className="text-tertiary hover:text-error-500 p-1"
                                            >
                                                <XCircle className="size-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Notes */}
                                <div className="px-3">
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-tertiary mb-1">Notes</label>
                                    <textarea
                                        value={gtNotes}
                                        onChange={(e) => { setGtNotes(e.target.value); setIsDirty(true); }}
                                        rows={2}
                                        placeholder="Annotation notes..."
                                        className="w-full px-3 py-2 text-sm text-primary bg-primary border border-secondary rounded-md
                                                   focus:outline-none focus:ring-2 focus:ring-brand/40 placeholder:text-quaternary resize-none"
                                    />
                                </div>

                                {/* Action buttons */}
                                <div className="flex gap-2 px-3 pt-2 border-t border-secondary">
                                    <Button
                                        onClick={handleSaveGroundTruth}
                                        size="sm" color="primary" iconLeading={Save01}
                                        isDisabled={saveGroundTruth.isPending}
                                        className="flex-1"
                                    >
                                        {saveGroundTruth.isPending ? 'Saving...' : 'Save Ground Truth'}
                                    </Button>
                                    <Button
                                        onClick={handleExportJson}
                                        size="sm" color="secondary" iconLeading={Download01}
                                    >
                                        Export
                                    </Button>
                                    <Button
                                        onClick={() => handleValidateDocument(TrainingStatusEnum.VALIDATED)}
                                        size="sm"
                                        color={document.training_status === TrainingStatusEnum.VALIDATED ? 'secondary' : 'primary'}
                                        iconLeading={CheckCircle}
                                        isDisabled={updateDocument.isPending || !document.ground_truth_path || document.training_status === TrainingStatusEnum.VALIDATED}
                                    >
                                        {document.training_status === TrainingStatusEnum.VALIDATED ? 'Validated' : 'Mark Validated'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* ═══ ACTIONS TAB ═══ */}
                        {activeTab === 'actions' && (
                            <div className="p-4 space-y-6">
                                {/* Document info */}
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-primary">Document Information</h4>
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-tertiary">Type</span>
                                            <p className="text-primary">{currentDocType?.name || document.document_type || '—'}</p>
                                        </div>
                                        <div>
                                            <span className="text-tertiary">Status</span>
                                            <div>{getStatusBadge(document.training_status as TrainingStatusEnum)}</div>
                                        </div>
                                        <div>
                                            <span className="text-tertiary">Dataset</span>
                                            <p className="text-primary">{document.dataset_name || '—'}</p>
                                        </div>
                                        <div>
                                            <span className="text-tertiary">Schema</span>
                                            <p className="text-primary">{schema?.schema_name || '—'}</p>
                                        </div>
                                        <div>
                                            <span className="text-tertiary">Version</span>
                                            <p className="text-primary">{document.dataset_version || '—'}</p>
                                        </div>
                                        <div>
                                            <span className="text-tertiary">Created</span>
                                            <p className="text-primary">{new Date(document.created_at).toLocaleDateString('it-IT')}</p>
                                        </div>
                                        {document.notes && (
                                            <div className="col-span-2">
                                                <span className="text-tertiary">Notes</span>
                                                <p className="text-primary">{document.notes}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Schema selector */}
                                {document.document_type && schemas.length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className="text-sm font-semibold text-primary">Schema</h4>
                                        <div className="flex items-end gap-2">
                                            <div className="flex-1">
                                                <Select
                                                    label="Document Schema"
                                                    items={schemas.map(s => ({ id: s.document_schema_id, label: s.schema_name, description: s.schema_code }))}
                                                    selectedKey={schema?.document_schema_id || ''}
                                                    onSelectionChange={async (key) => {
                                                        if (!trainingDocId || !key) return;
                                                        try {
                                                            await updateDocument.mutateAsync({
                                                                docId: trainingDocId,
                                                                data: { document_schema_id: String(key) },
                                                            });
                                                            toast.success('Schema updated');
                                                            refetchDocument();
                                                        } catch {
                                                            toast.error('Error updating schema');
                                                        }
                                                    }}
                                                    size="sm"
                                                >
                                                    {(item) => (
                                                        <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                            <div className="font-medium">{item.label}</div>
                                                            <div className="text-xs text-tertiary">{item.description}</div>
                                                        </Select.Item>
                                                    )}
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Dataset Name editor */}
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-primary">Dataset</h4>
                                    {editingDatasetName !== null ? (
                                        <div className="flex items-end gap-2">
                                            <div className="flex-1">
                                                <Input
                                                    label="Dataset name"
                                                    value={editingDatasetName}
                                                    onChange={(value) => setEditingDatasetName(value)}
                                                    placeholder="e.g. training_v2"
                                                    size="sm"
                                                />
                                            </div>
                                            <Button
                                                onClick={async () => {
                                                    if (!trainingDocId || !editingDatasetName.trim()) return;
                                                    try {
                                                        await updateDocument.mutateAsync({
                                                            docId: trainingDocId,
                                                            data: { dataset_name: editingDatasetName.trim() },
                                                        });
                                                        toast.success('Dataset name updated');
                                                        setEditingDatasetName(null);
                                                        refetchDocument();
                                                    } catch {
                                                        toast.error('Error updating dataset name');
                                                    }
                                                }}
                                                size="sm"
                                                color="primary"
                                                isDisabled={updateDocument.isPending || !editingDatasetName.trim() || editingDatasetName.trim() === document.dataset_name}
                                            >
                                                {updateDocument.isPending ? 'Saving...' : 'Save'}
                                            </Button>
                                            <Button
                                                onClick={() => setEditingDatasetName(null)}
                                                size="sm"
                                                color="secondary"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-primary">{document.dataset_name || 'default'}</span>
                                            <button
                                                onClick={() => setEditingDatasetName(document.dataset_name || 'default')}
                                                className="p-1 rounded text-tertiary hover:text-primary hover:bg-secondary transition-colors"
                                                title="Edit dataset name"
                                            >
                                                <Edit05 className="size-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Extraction */}
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-primary">Run Extraction</h4>
                                    <div className="flex items-end gap-2">
                                        <div className="flex-1">
                                            <Select
                                                label="Strategy"
                                                items={extractionStrategies.map(s => ({ id: s.name, label: s.name, description: s.description }))}
                                                selectedKey={selectedStrategy}
                                                onSelectionChange={(key) => setSelectedStrategy(key ? String(key) : 'vision')}
                                                size="sm"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                        <div className="font-medium">{item.label}</div>
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>
                                        <Button onClick={handleRunExtraction} size="sm" color="primary" iconLeading={Play} isDisabled={runExtraction.isPending}>
                                            {runExtraction.isPending ? 'Running...' : 'Extract'}
                                        </Button>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={ocrOnly}
                                            onChange={(e) => setOcrOnly(e.target.checked)}
                                            className="rounded border-secondary text-brand focus:ring-brand"
                                        />
                                        <span className="text-sm text-secondary">OCR Only (skip field storage)</span>
                                    </label>
                                </div>

                                {/* Classification */}
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-primary">Classification</h4>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <Button onClick={handleRunClassification} size="sm" color="secondary" iconLeading={Tag01} isDisabled={classifyDocument.isPending}>
                                            {classifyDocument.isPending ? 'Classifying...' : 'Auto Classify'}
                                        </Button>
                                        <span className="text-xs text-tertiary self-center">or</span>
                                        <div className="flex items-center gap-2 flex-1">
                                            <Select.ComboBox
                                                selectedKey={manualDocTypeId}
                                                onSelectionChange={(key) => setManualDocTypeId(key as string)}
                                                placeholder="Search type..."
                                                className="min-w-[180px]"
                                                shortcut={false}
                                                items={documentTypes.map((dt) => ({
                                                    id: dt.document_type_id,
                                                    label: dt.name,
                                                    supportingText: dt.key,
                                                }))}
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                        <span className="text-primary">{item.label}</span>
                                                    </Select.Item>
                                                )}
                                            </Select.ComboBox>
                                            <Button onClick={handleSetManualDocType} size="sm" color="secondary" isDisabled={!manualDocTypeId || updateDocument.isPending}>
                                                Set
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ═══ HISTORY TAB ═══ */}
                        {activeTab === 'history' && trainingDocId && (
                            <div className="p-4">
                                <JobHistorySection
                                    trainingDocId={trainingDocId}
                                    jobs={jobs}
                                    isLoading={isLoadingJobs}
                                    isError={isJobsError}
                                    isCollapsed={false}
                                    onToggleCollapse={() => {}}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Split Document Modal */}
            <SplitDocumentModal
                isOpen={isSplitModalOpen}
                onClose={() => setIsSplitModalOpen(false)}
                onConfirm={handleSplitConfirm}
                isLoading={splitDocument.isPending}
                documentTypes={detectedDocumentTypes}
                fileName={document.file_path.split('/').pop()}
            />
        </div>
    );
};
